import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { messageRepository, services } from '@/lib/services';

export interface MessageAttachment {
  url: string;
  name?: string;
  type?: string;
}

// Message Types
export interface Message {
  id?: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  recipientId: string;
  recipientName: string;
  recipientAvatar?: string;
  content: string;
  jobId?: string;
  jobTitle?: string;
  jobType?: 'existing' | 'new-request';
  messageType?: 'application' | 'inquiry' | 'general';
  resumeUrl?: string;
  resumeName?: string;
  attachments?: MessageAttachment[];
  read: boolean;
  createdAt: string | Timestamp;
  updatedAt: string | Timestamp;
}

export interface Conversation {
  id?: string;
  participants: string[];
  participantNames: { [userId: string]: string };
  participantAvatars?: { [userId: string]: string };
  lastMessage?: string;
  lastMessageTime?: string | Timestamp;
  lastMessageSenderId?: string;
  jobId?: string;
  jobTitle?: string;
  unreadCount: { [userId: string]: number };
  createdAt: string | Timestamp;
  updatedAt: string | Timestamp;
}

export interface MessageRequest {
  recipientId: string;
  recipientName: string;
  content: string;
  jobId?: string;
  jobTitle?: string;
  jobType?: 'existing' | 'new-request';
}

class MessagesService {
  private readonly messagesCollection = collection(db, 'messages');
  private readonly conversationsCollection = collection(db, 'conversations');
  private sendMessageNotificationEmailFunction = httpsCallable(functions, 'sendMessageNotificationEmail');

  private async getUserDetails(userId: string): Promise<{ email?: string; name?: string }> {
    try {
      const userData = await services.admin.getUserById(userId);
      return {
        email: userData?.email,
        name: userData?.firstName || userData?.username || 'User',
      };
    } catch (error) {
      console.error('Error getting user details for message notification:', error);
      return {};
    }
  }

  private async sendMessageEmailNotification(
    recipientId: string,
    senderName: string,
    messageContent: string,
    conversationId: string,
    jobTitle?: string,
  ): Promise<void> {
    try {
      const recipientDetails = await this.getUserDetails(recipientId);

      if (!recipientDetails.email) {
        console.log('No email found for recipient, skipping message notification email');
        return;
      }

      await this.sendMessageNotificationEmailFunction({
        recipientEmail: recipientDetails.email,
        recipientName: recipientDetails.name || 'User',
        senderName,
        messagePreview:
          messageContent.length > 200 ? messageContent.substring(0, 200) + '...' : messageContent,
        conversationId,
        jobTitle,
      });

      console.log('[messages] Message notification email sent successfully');
    } catch (error) {
      console.error('Error sending message notification email:', error);
    }
  }

  async getOrCreateConversation(
    userId: string,
    userName: string,
    recipientId: string,
    recipientName: string,
    jobId?: string,
    jobTitle?: string,
    userAvatar?: string,
    recipientAvatar?: string,
  ): Promise<string> {
    // Self-messaging guard (chokepoint — every contact/apply/message flow comes
    // through here). Reported: contacting your own profile / applying to your
    // own job opened a conversation with yourself and even emailed you.
    if (userId && recipientId && userId === recipientId) {
      throw new Error("You can't send a message to yourself.");
    }
    return messageRepository.getOrCreateConversation(
      userId,
      userName,
      recipientId,
      recipientName,
      jobId,
      jobTitle,
      userAvatar,
      recipientAvatar,
    );
  }

  async sendMessage(
    senderId: string,
    senderName: string,
    recipientId: string,
    recipientName: string,
    content: string,
    jobId?: string,
    jobTitle?: string,
    jobType?: 'existing' | 'new-request',
    senderAvatar?: string,
    recipientAvatar?: string,
    messageType?: 'application' | 'inquiry' | 'general',
    attachmentData?: { resumeUrl?: string; resumeName?: string; attachments?: MessageAttachment[] },
  ): Promise<string> {
    try {
      const conversationId = await this.getOrCreateConversation(
        senderId,
        senderName,
        recipientId,
        recipientName,
        jobId,
        jobTitle,
        senderAvatar,
        recipientAvatar,
      );

      const sanitizedAttachments = (attachmentData?.attachments || [])
        .filter((attachment) => typeof attachment?.url === 'string' && attachment.url.trim() !== '')
        .map((attachment) => ({
          url: attachment.url,
          ...(attachment.name ? { name: attachment.name } : {}),
          ...(attachment.type ? { type: attachment.type } : {}),
        }));

      const messagePreview =
        content?.trim() ||
        (sanitizedAttachments.length > 0
          ? `[${sanitizedAttachments.length} attachment${sanitizedAttachments.length > 1 ? 's' : ''}]`
          : 'New message');

      const message: Omit<Message, 'id'> = {
        conversationId,
        senderId,
        senderName,
        recipientId,
        recipientName,
        content,
        read: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(jobId && { jobId }),
        ...(jobTitle && { jobTitle }),
        ...(jobType && { jobType }),
        ...(messageType && { messageType }),
        ...(senderAvatar && { senderAvatar }),
        ...(recipientAvatar && { recipientAvatar }),
        ...(attachmentData?.resumeUrl && { resumeUrl: attachmentData.resumeUrl }),
        ...(attachmentData?.resumeName && { resumeName: attachmentData.resumeName }),
        ...(sanitizedAttachments.length > 0 && { attachments: sanitizedAttachments }),
      };

      const messageId = await messageRepository.createThreadMessage({
        ...message,
      } as Record<string, unknown>);

      // Atomically bump the recipient's unread counter on the conversation
      // doc. We used to re-query the messages collection to derive the
      // count and write it back, but that meant the badge drifted to 0
      // any time the count query hit a transient permission/index error
      // (intermittent Firestore static-rule check on multi-field
      // queries). Using `FieldValue.increment(1)` keeps the badge truthful
      // even when the read-side query fails, and it never goes negative
      // because `markConversationMessagesAsRead` just sets it back to 0.
      await messageRepository.updateConversationDocument(conversationId, {
        lastMessage: messagePreview,
        lastMessageTime: serverTimestamp(),
        lastMessageSenderId: senderId,
        [`unreadCount.${recipientId}`]: increment(1),
        updatedAt: serverTimestamp(),
      });

      this.sendMessageEmailNotification(
        recipientId,
        senderName,
        messagePreview,
        conversationId,
        jobTitle,
      ).catch((error) => {
        console.error('Failed to send message notification email:', error);
      });

      return messageId;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async getUserConversations(userId: string): Promise<Conversation[]> {
    const rows = await messageRepository.getUserConversations(userId);
    return rows as Conversation[];
  }

  async getConversationMessages(conversationId: string): Promise<Message[]> {
    const rows = await messageRepository.getConversationMessages(conversationId);
    return rows as unknown as Message[];
  }

  async markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      await messageRepository.markConversationMessagesAsRead(conversationId, userId);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }

  // Real-time message stream for a thread the caller is a participant in.
  //
  // The Firestore rule on /messages requires
  //   senderId == auth.uid OR recipientId == auth.uid
  // and Firestore rejects any onSnapshot whose query can't statically prove
  // every returned doc satisfies that rule. A single
  //   where('conversationId', '==', X)
  // listener fails the rule check (no senderId/recipientId filter) and dies
  // with permission-denied — which is what was happening when users opened
  // a thread.
  //
  // Fix: run two listeners — one for messages I sent, one for messages I
  // received — and merge in memory. Each individual query statically
  // satisfies the rule (it filters on senderId or recipientId == auth.uid),
  // so both listeners subscribe cleanly. In a 1:1 thread these two streams
  // together cover every message.
  subscribeToMessages(
    conversationId: string,
    userId: string,
    callback: (messages: Message[]) => void,
  ) {
    const sentQ = query(
      this.messagesCollection,
      where('conversationId', '==', conversationId),
      where('senderId', '==', userId),
      orderBy('createdAt', 'asc'),
    );
    const receivedQ = query(
      this.messagesCollection,
      where('conversationId', '==', conversationId),
      where('recipientId', '==', userId),
      orderBy('createdAt', 'asc'),
    );

    const byId = new Map<string, Message>();
    const emit = () => {
      const merged = Array.from(byId.values()).sort((a, b) => {
        const at = (a.createdAt as Timestamp | undefined)?.seconds || 0;
        const bt = (b.createdAt as Timestamp | undefined)?.seconds || 0;
        return at - bt;
      });
      callback(merged);
    };

    const ingest = (snapshot: { docs: Array<{ id: string; data(): unknown }> }) => {
      snapshot.docs.forEach((d) => {
        byId.set(d.id, { id: d.id, ...(d.data() as Record<string, unknown>) } as Message);
      });
      emit();
    };

    const onErr = (label: string) => (error: unknown) => {
      console.error(`Error in message subscription (${label}):`, error);
    };

    const unsubSent = onSnapshot(sentQ, ingest, onErr('sent'));
    const unsubReceived = onSnapshot(receivedQ, ingest, onErr('received'));

    return () => {
      unsubSent();
      unsubReceived();
    };
  }

  // Filter out conversations the user has soft-deleted unless the OTHER
  // party has sent a new message AFTER the delete. Hide-on-delete is
  // marked by `deletedFor.<uid>` on the conversation doc (set by
  // `deleteConversationCascade`); a new inbound message bumps
  // `lastMessageTime` past the delete timestamp, which un-hides the
  // thread so the user sees the new contact (correct UX) — but the
  // PRIOR thread content stays gone for them. Without this filter the
  // entire deleted thread reappeared the moment the other side sent
  // anything, which was the "deleted conversations reappear" bug.
  private hideDeleted(conv: Conversation, userId: string): boolean {
    const deletedFor = (conv as Conversation & { deletedFor?: Record<string, { seconds?: number } | string> }).deletedFor;
    if (!deletedFor) return false;
    const raw = deletedFor[userId];
    if (!raw) return false;
    const deletedAtMs =
      typeof raw === 'object' && raw && 'seconds' in raw
        ? (raw.seconds as number) * 1000
        : typeof raw === 'string'
          ? new Date(raw).getTime()
          : 0;
    if (!deletedAtMs) return true; // marker present but un-parseable — hide
    const lmt = conv.lastMessageTime as { seconds?: number } | string | undefined;
    const lastMsgMs =
      typeof lmt === 'object' && lmt && 'seconds' in lmt
        ? (lmt.seconds as number) * 1000
        : typeof lmt === 'string'
          ? new Date(lmt).getTime()
          : 0;
    return lastMsgMs <= deletedAtMs;
  }

  subscribeToConversations(userId: string, callback: (conversations: Conversation[]) => void) {
    const q = query(
      this.conversationsCollection,
      where('participants', 'array-contains', userId),
      orderBy('lastMessageTime', 'desc'),
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const conversations = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }) as Conversation)
          .filter((c) => !this.hideDeleted(c, userId));
        callback(conversations);
      },
      (error) => {
        // Fallback path is intentional and handled — demote to warn so the
        // consoleErrorBridge doesn't surface it as a Sentry alert (#16).
        console.warn('Conversation subscription falling back to unordered query:', error);

        const fallbackQ = query(
          this.conversationsCollection,
          where('participants', 'array-contains', userId),
        );

        onSnapshot(fallbackQ, (snapshot) => {
          const conversations = snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }) as Conversation)
            .filter((c) => !this.hideDeleted(c, userId));

          const sortedConversations = conversations.sort((a, b) => {
            const aTime = a.lastMessageTime
              ? typeof a.lastMessageTime === 'string'
                ? new Date(a.lastMessageTime).getTime()
                : a.lastMessageTime.seconds * 1000
              : 0;
            const bTime = b.lastMessageTime
              ? typeof b.lastMessageTime === 'string'
                ? new Date(b.lastMessageTime).getTime()
                : b.lastMessageTime.seconds * 1000
              : 0;
            return bTime - aTime;
          });

          callback(sortedConversations);
        });
      },
    );
  }

  async getJobApplications(jobId: string, jobCreatorId: string): Promise<Message[]> {
    const rows = await messageRepository.getJobApplicationMessages(jobId, jobCreatorId);
    return rows as Message[];
  }

  async updateMessage(messageId: string, updateData: Partial<Message>): Promise<void> {
    try {
      await messageRepository.patchThreadMessage(messageId, updateData as Record<string, unknown>);
      console.log('Message updated successfully:', messageId);
    } catch (error) {
      console.error('Error updating message:', error);
      throw error;
    }
  }

  async deleteConversation(conversationId: string, callerUserId: string): Promise<void> {
    try {
      await messageRepository.deleteConversationCascade(conversationId, callerUserId);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }
}

export const messagesService = new MessagesService();
export default messagesService;
